import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import type { Client } from '../types';

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    console.log('Clients: Fetching clients...');
    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Clients: Current user:', user);
      
      if (!user) {
        console.log('Clients: No user found, skipping fetch');
        setClients([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Clients: Error fetching:', error);
      } else {
        console.log('Clients: Fetched successfully:', data?.length || 0, 'clients');
        setClients(data || []);
      }
    } catch (err) {
      console.error('Clients: Exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const createClient = async (clientData: Omit<Client, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('clients')
      .insert([{ ...clientData, user_id: user.id }])
      .select()
      .single();

    if (error) throw error;
    setClients(prev => [data, ...prev]);
    return data;
  };

  const updateClient = async (id: string, updates: Partial<Client>) => {
    const { data, error } = await supabase
      .from('clients')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    setClients(prev => prev.map(c => c.id === id ? data : c));
    return data;
  };

  const deleteClient = async (id: string) => {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);

    if (error) throw error;
    setClients(prev => prev.filter(c => c.id !== id));
  };

  const searchClients = (query: string) => {
    const lowerQuery = query.toLowerCase();
    return clients.filter(c => 
      c.name.toLowerCase().includes(lowerQuery) ||
      c.email?.toLowerCase().includes(lowerQuery) ||
      c.phone?.includes(lowerQuery) ||
      c.company?.toLowerCase().includes(lowerQuery)
    );
  };

  const filterByStatus = (status: string) => {
    if (status === 'all') return clients;
    return clients.filter(c => c.status === status);
  };

  const filterBySource = (source: string) => {
    if (source === 'all') return clients;
    return clients.filter(c => c.source === source);
  };

  return {
    clients,
    loading,
    createClient,
    updateClient,
    deleteClient,
    fetchClients,
    searchClients,
    filterByStatus,
    filterBySource,
  };
};
